import { NavigateFunction } from 'react-router-dom';
import { APP_PATHS } from 'setup/navigation/paths';

interface NavigationOptions {
  replace?: boolean;
  state?: any;
}

// IMPORTANT: use arrow functions for methods to avoid binding issues
export class AppNavigationService {
  constructor(private navigate: NavigateFunction) {}

  private goToPath = <T extends string[]>(
    path: string | ((...args: T) => string),
    options?: NavigationOptions,
    params?: T
  ): void => {
    const finalPath =
      typeof path === 'string' ? path : path(...(params!));
    this.navigate(finalPath, {
      replace: options?.replace,
      state: options?.state,
    });
  };

  goToIssues = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.issue.root, options);
  };

  goToIssueDetails = (
    projectId: string,
    issueId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.issue.single, options, [projectId, issueId]);
  };

  goToIssueEventDetails = (
    projectId: string,
    issueId: string,
    eventId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.issue.event, options, [
      projectId,
      issueId,
      eventId,
    ]);
  };

  goToIssueDecision = (
    projectId: string,
    issueId: string,
    decisionId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.issue.decision, options, [
      projectId,
      issueId,
      decisionId,
    ]);
  };

  // Company routes
  goToCompanies = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.company.root, options);
  };

  goToCreateCompany = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.company.create, options);
  };

  /** A company has no separate details view: its details open in the edit view. */
  goToCompanyDetails = (id: string, options?: NavigationOptions): void => {
    this.goToCompanyEdit(id, options);
  };

  goToCompanyEdit = (id: string, options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.company.edit, options, [id]);
  };

  // Contract routes
  goToContracts = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.contract.root, options);
  };

  goToCreateContract = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.contract.create, options);
  };

  goToContractDetails = (
    id: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.contract.details, options, [id]);
  };

  goToContractEdit = (id: string, options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.contract.edit, options, [id]);
  };

  // Inspection routes
  goToInspections = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.inspection.root, options);
  };

  goToCreateInspection = (
    templateId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspection.create, options, [templateId]);
  };

  goToInspectionDetails = (
    projectId: string,
    inspectionId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspection.single, options, [
      projectId,
      inspectionId,
    ]);
  };

  goToInspectionEdit = (
    projectId: string,
    inspectionId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspection.edit, options, [
      projectId,
      inspectionId,
    ]);
  };

  goToInspectionProtocolItem = (
    projectId: string,
    inspectionId: string,
    protocolItemId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspection.protocolItem, options, [
      projectId,
      inspectionId,
      protocolItemId,
    ]);
  };

  goToInspectionDocument = (
    projectId: string,
    inspectionId: string,
    documentId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspection.document.onInspection, options, [
      projectId,
      inspectionId,
      documentId,
    ]);
  };

  goToInspectionProtocolDocument = (
    projectId: string,
    inspectionId: string,
    protocolId: string,
    documentId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspection.document.onProtocol, options, [
      projectId,
      inspectionId,
      protocolId,
      documentId,
    ]);
  };

  // Inspection Template routes
  goToInspectionTemplates = (
    projectId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspectionTemplate.root, options, [projectId]);
  };

  goToCreateInspectionTemplate = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.inspectionTemplate.create, options);
  };

  goToInspectionTemplateDetails = (
    projectId: string,
    id: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspectionTemplate.single, options, [
      projectId,
      id,
    ]);
  };

  goToInspectionTemplateEdit = (
    id: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspectionTemplate.edit, options, [id]);
  };

  goToInspectionTemplateChecklistItem = (
    projectId: string,
    id: string,
    checklistItemId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.inspectionTemplate.checklistItem, options, [
      projectId,
      id,
      checklistItemId,
    ]);
  };

  goToInspectionTemplateDocument = (
    projectId: string,
    id: string,
    documentId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(
      APP_PATHS.inspectionTemplate.document.onTemplate,
      options,
      [projectId, id, documentId]
    );
  };

  goToInspectionTemplateChecklistDocument = (
    projectId: string,
    id: string,
    checklistId: string,
    documentId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(
      APP_PATHS.inspectionTemplate.document.onChecklistItem,
      options,
      [projectId, id, checklistId, documentId]
    );
  };

  // Project and organization settings routes
  goToProjectEdit = (
    projectId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.project.edit, options, [projectId]);
  };

  goToProjectSettings = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.project.settings.root, options);
  };

  goToOrganizationDetails = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.project.settings.organizationDetails, options);
  };

  goToProjectDetails = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.project.settings.projectDetails, options);
  };

  goToProjectSelect = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.project.select, options);
  };

  // User routes
  goToUsers = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.user.root, options);
  };

  goToCreateUser = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.user.create, options);
  };

  goToCurrentUser = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.user.current, options);
  };

  goToUserDetails = (id: string, options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.user.single, options, [id]);
  };

  goToUserEdit = (id: string, options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.user.edit, options, [id]);
  };

  // Fields Settings routes
  goToFieldsSettings = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.fieldsSettings.root, options);
  };

  goToCustomField = (
    fieldId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.fieldsSettings.customField, options, [
      fieldId,
    ]);
  };

  // Documentation routes
  goToDocumentation = (
    projectId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.documentation.root(projectId), options);
  };

  goToDocumentationFile = (
    projectId: string,
    fileId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.documentation.file, options, [
      projectId,
      fileId,
    ]);
  };

  goToDocumentationDirectory = (
    projectId: string,
    directoryId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.documentation.dir, options, [
      projectId,
      directoryId,
    ]);
  };

  // Report routes
  goToReportDownload = (
    reportId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.report.download, options, [reportId]);
  };

  goToOrganizationEdit = (
    id: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.organization.edit, options, [id]);
  };

  goToSelectProject = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.project.select, options);
  };

  goToAnalytics = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.analytics.root, options);
  };

  // Workflow routes
  goToFieldsVisibility = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.project.settings.fieldVisibility, options);
  };

  goToWorkflowConfiguration = (options?: NavigationOptions): void => {
    this.goToPath(APP_PATHS.project.settings.workflowConfiguration, options);
  };

  goToWorkflowCreator = (
    projectId: string,
    workflowId: string,
    options?: NavigationOptions
  ): void => {
    this.goToPath(APP_PATHS.project.settings.workflowCreator, options, [
      projectId,
      workflowId,
    ]);
  };

  // Basic navigation methods
  goBack = (): void => {
    this.navigate(-1);
  };

  goRelative = (path: string, options?: NavigationOptions): void => {
    this.navigate(path, {
      relative: 'route',
      replace: options?.replace,
      state: options?.state,
    });
  };
}
